import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Component } from "react";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import Layout from "@/components/Layout";
import Login from "@/pages/Login";
import EnterpriseDashboard from "@/pages/EnterpriseDashboard";
import CrewFatigue from "@/pages/CrewFatigue";
import EngineeringRisk from "@/pages/EngineeringRisk";
import CAMOPlanning from "@/pages/CAMOPlanning";
import BackOffice from "@/pages/BackOffice";
import GroundOps from "@/pages/GroundOps";
import SafetyAnalytics from "@/pages/SafetyAnalytics";
import IrropsConsole from "@/pages/IrropsConsole";
import AlertsCentre from "@/pages/AlertsCentre";
import AdminSettings from "@/pages/AdminSettings";
import CrewMaster from "@/pages/crewops/CrewMaster";
import RosterPlanner from "@/pages/crewops/RosterPlanner";
import PairingBuilder from "@/pages/crewops/PairingBuilder";
import ComplianceBoard from "@/pages/crewops/ComplianceBoard";
import RecoveryConsole from "@/pages/crewops/RecoveryConsole";
import AuditLog from "@/pages/crewops/AuditLog";
import ScenarioSimulator from "@/pages/crewops/ScenarioSimulator";
import DisruptionConsole from "@/pages/crewops/DisruptionConsole";
import StaffingEngine from "@/pages/StaffingEngine";
import FlightOpsBoard from "@/pages/FlightOpsBoard";
import OpsMap from "@/pages/OpsMap";
import TrainingEngine from "@/pages/crewops/TrainingEngine";
import FairnessEngine from "@/pages/crewops/FairnessEngine";
import OpsConsole from "@/pages/crewops/OpsConsole";
import SwapRequests from "@/pages/crewops/SwapRequests";
import PresentationMode from "@/pages/PresentationMode";
import OperationsManual from "@/pages/OperationsManual";
import IntegrationsHub from "@/pages/IntegrationsHub";
import CrewDeployment from "@/pages/crewops/CrewDeployment";
import MonthlyScheduler from "@/pages/crewops/MonthlyScheduler";
import FRMSModule from "@/pages/crewops/FRMSModule";
import MarketingBrochure from "@/pages/MarketingBrochure";
import MySectors from "@/pages/MySectors";
import PilotAccess from "@/pages/PilotAccess";
import PricingCalculator from "@/pages/PricingCalculator";
import TurnaroundLayout from "@/pages/groundops/TurnaroundLayout";
import TurnaroundBoard from "@/pages/groundops/TurnaroundBoard";
import TurnaroundDetail from "@/pages/groundops/TurnaroundDetail";
import ExceptionView from "@/pages/groundops/ExceptionView";
import StationDashboard from "@/pages/groundops/StationDashboard";
import VendorSLA from "@/pages/groundops/VendorSLA";
import GroundStaffPlanning from "@/pages/groundops/GroundStaffPlanning";
import GroundFatigueShield from "@/pages/groundops/GroundFatigueShield";
import RampApp from "@/pages/groundops/ramp/RampApp";
import MarketingHome from "@/pages/marketing/MarketingHome";
import FatigueShieldHome from "@/pages/marketing/FatigueShieldHome";
import CrewRosteringPage from "@/pages/marketing/CrewRosteringPage";
import AnalyticsPage from "@/pages/marketing/AnalyticsPage";
import FlightOpsPage from "@/pages/marketing/FlightOpsPage";
import CompliancePage from "@/pages/marketing/CompliancePage";
import MarketingModule from "@/pages/marketing/MarketingModule";
import MarketingDemo from "@/pages/marketing/MarketingDemo";
import MarketingPlatform from "@/pages/marketing/MarketingPlatform";
import DemoRequests from "@/pages/DemoRequests";
import FatigueTheatre from "@/pages/FatigueTheatre";
import ProductTree from "@/pages/ProductTree";
import "@/App.css";

// Global error boundary – prevents ANY unhandled render crash from showing a blank page
class AppErrorBoundary extends Component {
  constructor(props) { super(props); this.state = { crashed: false, error: null }; }
  static getDerivedStateFromError(error) { return { crashed: true, error }; }
  componentDidCatch(error, info) { console.error("[AppError]", error, info); }
  render() {
    if (this.state.crashed) {
      return (
        <div style={{ minHeight: "100vh", background: "#060d1a", display: "flex", alignItems: "center", justifyContent: "center", flexDirection: "column", padding: "32px", textAlign: "center" }}>
          <div style={{ color: "#f59e0b", fontSize: "32px", marginBottom: "16px" }}>⚠</div>
          <div style={{ color: "#f8fafc", fontWeight: 700, fontSize: "16px", marginBottom: "8px" }}>Something went wrong</div>
          <div style={{ color: "#94a3b8", fontSize: "12px", maxWidth: "420px", marginBottom: "4px" }}>{this.state.error?.message || "An unexpected error occurred"}</div>
          <div style={{ color: "#64748b", fontSize: "11px", marginBottom: "24px", fontFamily: "monospace" }}>Check the browser console for details</div>
          <button onClick={() => window.location.reload()}
            style={{ padding: "8px 20px", background: "transparent", border: "1px solid #334155", borderRadius: "4px", color: "#94a3b8", cursor: "pointer", fontSize: "12px" }}>
            Reload Page
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return (
    <div className="min-h-screen bg-background flex items-center justify-center">
      <div className="text-primary font-mono text-sm animate-pulse">INITIALISING FATIGUE SHIELD...</div>
    </div>
  );
  return user ? children : <Navigate to="/login" />;
}

/**
 * AppShell — root element for the "/" parent route.
 * Handles three cases without any pathless layout ambiguity:
 *   1. Loading         → spinner
 *   2. "/" + no user   → MarketingHome (public landing)
 *   3. "/" + user      → redirect to dashboard
 *   4. "/anything" + no user → redirect to /login
 *   5. "/anything" + user   → render Layout (Outlet)
 */
function AppShell() {
  const { user, loading } = useAuth();
  const location = useLocation();
  const defaultPath = user?.role === "pilot" || user?.role === "ame" ? "/crew" : "/dashboard";

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-primary font-mono text-sm animate-pulse">INITIALISING FATIGUE SHIELD...</div>
      </div>
    );
  }

  // Root "/" — marketing for guests, redirect for signed-in
  if (location.pathname === "/") {
    return user ? <Navigate to={defaultPath} replace /> : <FatigueShieldHome />;
  }

  // All other app paths — require auth
  if (!user) return <Navigate to="/login" replace state={{ from: location }} />;

  // Authenticated — render the sidebar app shell
  return <Layout />;
}

function AppRoutes() {
  return (
    <Routes>
      {/* ── Login (public) ────────────────────────────────────────────── */}
      <Route path="/login" element={<Login />} />
      {/* ── Fatigue Scenario Theatre — public shareable link ── */}
      <Route path="/fatigue-theatre" element={<FatigueTheatre />} />
      <Route path="/product-tree" element={<ProductTree />} />

      {/* ── Public marketing pages ────────────────────────────────────── */}
      <Route path="/marketing" element={<Navigate to="/" replace />} />
      <Route path="/ground-ops" element={<MarketingHome />} />
      <Route path="/crew-rostering" element={<CrewRosteringPage />} />
      <Route path="/analytics" element={<AnalyticsPage />} />
      <Route path="/flight-ops" element={<FlightOpsPage />} />
      <Route path="/compliance" element={<CompliancePage />} />
      <Route path="/marketing/platform" element={<MarketingPlatform />} />
      <Route path="/marketing/module/:slug" element={<MarketingModule />} />
      <Route path="/marketing/demo" element={<MarketingDemo />} />

      {/* ── Turnaround Control Tower (standalone layout) ──────────────── */}
      <Route path="/turnaround" element={<ProtectedRoute><TurnaroundLayout /></ProtectedRoute>}>
        <Route index element={<Navigate to="/turnaround/board" replace />} />
        <Route path="board" element={<TurnaroundBoard />} />
        <Route path="flight/:id" element={<TurnaroundDetail />} />
        <Route path="exceptions" element={<ExceptionView />} />
        <Route path="stations" element={<StationDashboard />} />
        <Route path="vendors" element={<VendorSLA />} />
        <Route path="staff-planning" element={<GroundStaffPlanning />} />
        <Route path="ground-fatigue" element={<GroundFatigueShield />} />
      </Route>

      {/* ── Ramp Mobile PWA ───────────────────────────────────────────── */}
      <Route path="/ramp/*" element={<ProtectedRoute><RampApp /></ProtectedRoute>} />

      {/* ── Root + all app routes via AppShell ────────────────────────── */}
      {/* AppShell handles: marketing home for guests, auth guard for app  */}
      <Route path="/" element={<AppShell />}>
        <Route path="dashboard" element={<EnterpriseDashboard />} />
        <Route path="crew" element={<CrewFatigue />} />
        <Route path="engineering" element={<EngineeringRisk />} />
        <Route path="camo" element={<CAMOPlanning />} />
        <Route path="back-office" element={<BackOffice />} />
        <Route path="ground-ops" element={<GroundOps />} />
        <Route path="safety" element={<SafetyAnalytics />} />
        <Route path="irrops" element={<IrropsConsole />} />
        <Route path="alerts" element={<AlertsCentre />} />
        <Route path="admin" element={<AdminSettings />} />
        {/* Crew Rostering */}
        <Route path="roster/crew" element={<CrewMaster />} />
        <Route path="roster/planner" element={<RosterPlanner />} />
        <Route path="roster/pairings" element={<PairingBuilder />} />
        <Route path="roster/compliance" element={<ComplianceBoard />} />
        <Route path="roster/recovery" element={<RecoveryConsole />} />
        <Route path="roster/audit" element={<AuditLog />} />
        <Route path="roster/simulator" element={<ScenarioSimulator />} />
        <Route path="roster/disruption" element={<DisruptionConsole />} />
        <Route path="roster/deployment" element={<CrewDeployment />} />
        <Route path="roster/monthly-scheduler" element={<MonthlyScheduler />} />
        <Route path="staffing-engine" element={<StaffingEngine />} />
        <Route path="training-engine" element={<TrainingEngine />} />
        <Route path="fairness-engine" element={<FairnessEngine />} />
        <Route path="ops-console" element={<OpsConsole />} />
        <Route path="swap-requests" element={<SwapRequests />} />
        <Route path="flights" element={<FlightOpsBoard />} />
        <Route path="ops-map" element={<OpsMap />} />
        <Route path="presentation" element={<PresentationMode />} />
        <Route path="operations-manual" element={<OperationsManual />} />
        <Route path="integrations" element={<IntegrationsHub />} />
        <Route path="frms" element={<FRMSModule />} />
        <Route path="marketing-brochure" element={<MarketingBrochure />} />
        <Route path="my-sectors" element={<MySectors />} />
        <Route path="pilot-access" element={<PilotAccess />} />
        <Route path="pricing-calculator" element={<PricingCalculator />} />
        <Route path="demo-requests" element={<DemoRequests />} />
        {/* Fallback for unknown app paths */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Route>
    </Routes>
  );
}

export default function App() {
  return (
    <AppErrorBoundary>
      <AuthProvider>
        <BrowserRouter>
          <AppRoutes />
        </BrowserRouter>
      </AuthProvider>
    </AppErrorBoundary>
  );
}
